import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, imgSrc, primaryImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EthicsResultPanel } from "@/components/EthicsResultPanel";
import { RateSuggestionCard } from "@/components/RateSuggestionCard";
import { JobDescriptionForm } from "@/components/booking/JobDescriptionForm";
import { OfferForm } from "@/components/booking/OfferForm";
import { ArrowLeft, Building2, Pencil } from "lucide-react";

const EMPTY_JOB = {
  title: "",
  description: "",
  usage_type: "ecommerce",
  media_channels: [],
  duration_months: 3,
  exclusivity: false,
  retouching_requested: false,
};

export default function BookingFlow() {
  const { modelId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [model, setModel] = useState(null);
  const [draftId, setDraftId] = useState(params.get("draft") || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [job, setJob] = useState(EMPTY_JOB);
  const [screening, setScreening] = useState(null);
  const [screeningBusy, setScreeningBusy] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [note, setNote] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);

  useEffect(() => {
    api.get(`/models/${modelId}`).then(({ data }) => setModel(data)).catch(() => toast.error("Model not found"));
  }, [modelId]);

  const draftLoadedRef = useRef(false);
  useEffect(() => {
    const dId = params.get("draft");
    if (!dId || draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    api.get(`/drafts/${dId}`).then(({ data }) => {
      setDraftId(data.id);
      setJob({
        title: data.job?.title || "",
        description: data.job?.description || "",
        usage_type: data.job?.usage_type || "ecommerce",
        media_channels: data.job?.media_channels || [],
        duration_months: data.job?.duration_months || 3,
        exclusivity: data.job?.exclusivity || false,
        retouching_requested: data.job?.retouching_requested || false,
      });
      if (data.offer_amount) setOfferAmount(String(data.offer_amount));
      if (data.note) setNote(data.note);
      if (data.screening) setScreening(data.screening);
      toast.info("Draft loaded - continue editing or send when ready");
    }).catch(() => toast.error("Could not load draft"));
  }, [params]);

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const payload = {
        model_profile_id: modelId,
        job,
        offer_amount: offerAmount ? parseFloat(offerAmount) : null,
        note,
        screening_id: screening?.id || null,
      };
      if (draftId) {
        await api.put(`/drafts/${draftId}`, payload);
      } else {
        const { data } = await api.post("/drafts", payload);
        setDraftId(data.id);
      }
      toast.success("Draft saved - resume any time from your dashboard");
    } catch (e) {
      toast.error(errMsg(e, "Could not save draft"));
    } finally {
      setSavingDraft(false);
    }
  };

  const editJob = () => {
    setScreening(null);
    setAgreed(false);
    toast.info("Edit your proposal - you'll need to re-run the AI screening before sending");
  };

  const runScreening = async () => {
    if (!job.title || job.description.length < 10) {
      toast.error("Give the job a title and a description of at least 10 characters");
      return;
    }
    if (job.media_channels.length === 0) {
      toast.error("Select at least one media channel — your licence is limited to the media you declare");
      return;
    }
    setScreeningBusy(true);
    setScreening(null);
    try {
      const { data } = await api.post("/ai/screen-job", { model_profile_id: modelId, job });
      setScreening(data);
      if (data.ethics?.approved && data.rate_suggestion?.suggested_total) {
        setOfferAmount(String(Math.round(data.rate_suggestion.suggested_total)));
        toast.success("Job approved by AI screening");
      } else {
        toast.error("Job rejected by ethical screening — review the reasons below");
      }
    } catch (e) {
      toast.error(errMsg(e, "AI screening failed. Try again."));
    } finally {
      setScreeningBusy(false);
    }
  };

  const sendRequest = async () => {
    if (!agreed) {
      toast.error("You must accept the Ethical Usage Agreement");
      return;
    }
    const amount = parseFloat(offerAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid offer amount");
      return;
    }
    setSendBusy(true);
    try {
      await api.post("/bookings", {
        screening_id: screening.id,
        offer_amount: amount,
        note,
        ethical_agreement_accepted: true,
        draft_id: draftId,
      });
      toast.success(`Booking request sent to ${model.name}`);
      navigate("/bookings");
    } catch (e) {
      toast.error(errMsg(e, "Could not send booking request"));
    } finally {
      setSendBusy(false);
    }
  };

  if (!authLoading && (!user || user.role !== "client")) {
    return (
      <div className="page-container py-20 text-center">
        <p className="font-display text-2xl">Clients only</p>
        <p className="mt-2 text-sm text-muted-foreground">Sign in with a client account to book models.</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate("/auth")}>Sign in</Button>
      </div>
    );
  }

  const hasCompanyProfile = !!user?.client_profile;
  const img = model ? imgSrc(primaryImage(model)) : null;
  const approved = screening?.ethics?.approved;

  return (
    <div className="page-container max-w-4xl py-8 sm:py-12">
      <button data-testid="booking-back-button" onClick={() => navigate(-1)} className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground transition-[color] hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="mb-8 flex items-center gap-4">
        {img && (
          <div className="h-20 w-16 overflow-hidden rounded-lg border">
            <img src={img} alt={model?.name} className="h-full w-full object-cover" />
          </div>
        )}
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rent this look</p>
          <h1 className="font-display text-3xl tracking-[-0.01em]">{model?.name || "…"}</h1>
        </div>
      </div>

      {!hasCompanyProfile ? (
        <Card className="rounded-2xl border-2 border-[hsl(43,74%,70%)]">
          <CardContent className="flex flex-col items-start gap-3 p-8">
            <Building2 className="h-7 w-7 text-[hsl(43,60%,35%)]" />
            <h2 className="font-display text-xl">Complete your company profile first</h2>
            <p className="text-sm text-muted-foreground">
              Clients must upload their company information before describing a job and booking models.
              Our AI uses it to screen the job and suggest a fair rate.
            </p>
            <Button data-testid="booking-complete-profile-button" className="rounded-xl" onClick={() => navigate("/dashboard")}>
              Set up company profile
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* STEP 1: JOB FORM */}
          <JobDescriptionForm
            job={job}
            setJob={setJob}
            approved={approved}
            screeningBusy={screeningBusy}
            savingDraft={savingDraft}
            onScreen={runScreening}
            onSaveDraft={saveDraft}
          />

          {/* STEP 2: RESULTS */}
          {screening && (
            <div data-testid="booking-ethics-step" className="space-y-6">
              <EthicsResultPanel ethics={screening.ethics} />
              {approved && (
                <div data-testid="booking-rate-step" className="space-y-3">
                  <RateSuggestionCard rate={screening.rate_suggestion} />
                  <Button data-testid="booking-edit-proposal-button" type="button" variant="outline" className="rounded-xl border bg-card hover:bg-muted" onClick={editJob}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit proposal (requires re-screening)
                  </Button>
                </div>
              )}
              {!approved && (
                <Button data-testid="booking-edit-job-button" variant="outline" className="rounded-xl" onClick={() => setScreening(null)}>
                  Edit job and re-screen
                </Button>
              )}
            </div>
          )}

          {/* STEP 3: OFFER */}
          {approved && (
            <OfferForm
              screening={screening}
              offerAmount={offerAmount}
              setOfferAmount={setOfferAmount}
              note={note}
              setNote={setNote}
              agreed={agreed}
              setAgreed={setAgreed}
              sendBusy={sendBusy}
              savingDraft={savingDraft}
              onSend={sendRequest}
              onSaveDraft={saveDraft}
            />
          )}
        </div>
      )}
    </div>
  );
}
